// ============================================
// ERROR BOUNDARY COMPONENT
// Fängt alle JavaScript-Fehler in React ab
// ============================================

import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // State aktualisieren, damit nächster Render Fallback-UI zeigt
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Fehler loggen (später kann man das zu einem Service senden)
    console.error('💥 ERROR BOUNDARY CAUGHT:', error);
    console.error('📍 ERROR INFO:', errorInfo);
    
    // Fehler-Details speichern
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Optional: Fehler zu Analytics-Service senden
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    // State zurücksetzen und neu versuchen
    this.setState({ 
      hasError: false,
      error: null,
      errorInfo: null
    });
    
    // Seite neu laden als letzter Ausweg
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Fallback-UI wenn Fehler aufgetreten ist
      return (
        <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-6">
          <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">💥</div>
              <h1 className="text-3xl font-bold text-red-600 mb-2">
                Oops! Da ist etwas schiefgelaufen
              </h1>
              <p className="text-gray-600 text-lg">
                Die App ist auf einen unerwarteten Fehler gestoßen.
              </p>
            </div>

            {/* Fehler-Details (nur im Development-Modus sichtbar) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                <h3 className="font-bold text-red-800 mb-2">🐛 Debug-Info (nur in Development):</h3>
                <div className="text-sm text-red-700 font-mono overflow-x-auto">
                  <p className="mb-2"><strong>Fehler:</strong> {this.state.error.toString()}</p>
                  {this.state.errorInfo && (
                    <details className="mt-2">
                      <summary className="cursor-pointer font-semibold">Stack Trace anzeigen</summary>
                      <pre className="mt-2 text-xs whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            )}

            {/* Hilfreiche Tipps */}
            <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                <span>💡</span>
                Was du tun kannst:
              </h3>
              <ul className="space-y-2 text-blue-900">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">1.</span>
                  <span>Klicke auf "Neu starten" um die App neu zu laden</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">2.</span>
                  <span>Leere deinen Browser-Cache (Strg + Shift + R)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">3.</span>
                  <span>Versuche es mit einem anderen Browser</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">4.</span>
                  <span>Kontaktiere den Support, wenn das Problem weiterhin besteht</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleReset}
                className="flex-1 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all flex items-center justify-center gap-2"
              >
                <span className="text-xl">🔄</span>
                Neu starten
              </button>
              
              <button
                onClick={() => window.location.href = 'mailto:restroomadvisor@gmx.de?subject=App%20Fehler&body=Es%20ist%20ein%20Fehler%20aufgetreten'}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
              >
                <span className="text-xl">📧</span>
                Support kontaktieren
              </button>
            </div>

            {/* Zusätzliche Info */}
            <div className="mt-6 text-center text-sm text-gray-500">
              <p>Fehler-ID: {Date.now()}</p>
              <p className="mt-1">RestroomAdvisor v5.7</p>
            </div>
          </div>
        </div>
      );
    }

    // Wenn kein Fehler, render Children normal
    return this.props.children;
  }
}

export default ErrorBoundary;
